import { ChevronRight, Pill } from 'lucide-react';

import type { TradeStatusCounts } from '@/entities/business-relationship/api/businessRelationship.api';
import type { RelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { Badge, type BadgeProps } from '@/shared/components/ui/badge';
import { Card, CardHeader } from '@/shared/components/ui/card';

/** 카운트는 Figma처럼 두 자리로 맞춘다 (02, 11) */
const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * M-04 · M-05 거래 정보 카드 — 웹에서는 조회만 한다.
 * 의원급은 제약사, 병원급은 의약품 단위로 센다 (상세 tradeStatusCounts).
 * 카운트 카드(›)는 M-19 · M-20 거래 정보 드로어를 열어 그 섹션으로 스크롤하고,
 * 「놓치고 있는 의약품」 [목록 보기]는 M-22 드로어를 연다.
 */
export const TradeInfoCard = ({
  isHospital,
  counts,
  untradedApplicableCount,
  onOpenTrade,
  onOpenMissed,
}: {
  isHospital: boolean;
  counts: TradeStatusCounts;
  untradedApplicableCount: number;
  onOpenTrade: (tab: RelationshipGroup) => void;
  onOpenMissed: () => void;
}) => {
  const unit = isHospital ? '의약품' : '제약사';

  const cards: Array<{
    tab: RelationshipGroup;
    label: string;
    tone: BadgeProps['tone'];
    value: number;
  }> = [
    { tab: 'ongoing', label: '거래중', tone: 'success', value: counts.ongoing },
    {
      tab: 'applying',
      label: '필터링 신청',
      tone: 'warning',
      value: counts.applying,
    },
    { tab: 'closed', label: '종료·거절', tone: 'muted', value: counts.closed },
  ];
  const missed = untradedApplicableCount;

  return (
    <Card>
      <CardHeader
        title="거래 정보"
        description={`${isHospital ? '병원급' : '의원급'}이므로 거래 단위는 ${unit}입니다. 웹에서는 조회만 가능합니다.`}
        actions={
          <>
            <AppOnlyButton feature="filteringApply" className="w-[118px]">
              필터링 신청
            </AppOnlyButton>
            <AppOnlyButton feature="additionalFiltering" className="w-[118px]">
              추가 필터링
            </AppOnlyButton>
          </>
        }
      />

      {/* 목록은 웹에서 보고 신청만 앱으로 보낸다 (M-22). 0건이어도 빈 상태를 볼 수 있게 행을 둔다 */}
      <div className="mt-[18px] flex w-full items-center gap-3.5 rounded-lg border border-line-soft bg-subtle px-4 py-3.5 lg:px-[18px]">
        <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-primary-soft">
          <Pill className="size-4 text-primary" />
        </span>
        <span className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-sm leading-[21px] font-medium text-ink">
            놓치고 있는 의약품
          </span>
          <span className="text-xs leading-[18px] text-ink-6">
            {missed > 0
              ? `${missed}건이 추가 필터링 가능해요 — 신청은 앱에서 진행합니다`
              : '확정 처방 중 미거래 의약품을 확인할 수 있어요'}
          </span>
        </span>
        <button
          type="button"
          onClick={onOpenMissed}
          className="ml-auto flex shrink-0 items-center gap-0.5 rounded-md border border-line-strong bg-surface px-3 py-2 text-xs leading-[18px] font-medium text-ink-2 hover:bg-muted"
        >
          목록 보기
          <ChevronRight className="size-3.5" />
        </button>
      </div>

      <div className="mt-3.5 grid grid-cols-1 gap-2.5 sm:grid-cols-3 lg:gap-3.5">
        {cards.map((c) => (
          <button
            type="button"
            key={c.label}
            onClick={() => onOpenTrade(c.tab)}
            aria-label={`${c.label} ${unit} ${c.value}건 보기`}
            className="flex flex-col rounded-xl border border-line-soft bg-subtle px-[18px] py-4 text-left transition-colors outline-none hover:border-line-strong hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <div className="flex items-center">
              <Badge tone={c.tone}>{c.label}</Badge>
              <ChevronRight className="ml-auto size-4 text-[#bbb]" />
            </div>
            <p className="mt-3 text-[26px] leading-[39px] font-bold text-ink">
              {pad2(c.value)}
            </p>
            <p className="mt-0.5 text-[11px] leading-[17px] text-ink-6">
              {c.label} {unit}
            </p>
          </button>
        ))}
      </div>
    </Card>
  );
};
